import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SOCKET_EVENTS } from '../../../lib/websocket/events';
import type { TeamMessage, TeamMessageEvent } from '../../../lib/websocket/events';
import { useSocketEvent, useTeamChannelSubscription } from '../../../lib/websocket/hooks';
import { teamChatService } from '../services/teamChatService';
import type { UploadedMedia } from '../../../lib/api';

/**
 * Team group chat for the current store's default channel.
 *
 * Loads the channel + history via React Query, subscribes to the channel's
 * socket room, and keeps a live message list (deduped by id, sorted by
 * createdAt so a socket echo and the POST response can't misorder).
 */
export function useTeamChat() {
  const channelsQuery = useQuery({
    queryKey: ['team-channels'],
    queryFn: () => teamChatService.getChannels(),
    staleTime: 5 * 60 * 1000,
  });
  const channel = channelsQuery.data?.[0] ?? null;
  const channelId = channel?.id ?? null;

  const messagesQuery = useQuery({
    queryKey: ['team-messages', channelId],
    queryFn: () => teamChatService.getMessages(channelId as string),
    enabled: !!channelId,
  });

  const [messages, setMessages] = useState<TeamMessage[]>([]);
  useEffect(() => {
    if (messagesQuery.data) setMessages(messagesQuery.data);
  }, [messagesQuery.data]);

  useTeamChannelSubscription(channelId);

  const upsert = useCallback((msg: TeamMessage) => {
    setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
  }, []);

  const onNew = useCallback(
    (evt: TeamMessageEvent) => {
      if (!channelId || evt.channelId !== channelId) return;
      upsert(evt.message);
    },
    [channelId, upsert]
  );
  useSocketEvent(SOCKET_EVENTS.TEAM_MESSAGE_NEW, onNew);

  const [sending, setSending] = useState(false);
  const send = useCallback(
    async (content: string, media?: UploadedMedia) => {
      const text = content.trim();
      if (!channelId || (!text && !media)) return;
      setSending(true);
      try {
        // The reply (staff msg, or AI reply when @mentioned) also arrives over
        // the socket; upsert dedups so we never double-render.
        const msg = await teamChatService.sendMessage(channelId, text, media);
        upsert(msg);
      } finally {
        setSending(false);
      }
    },
    [channelId, upsert]
  );

  const sorted = useMemo(
    () =>
      [...messages].sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? '')),
    [messages]
  );

  return {
    channel,
    channelId,
    messages: sorted,
    isLoading: channelsQuery.isLoading || (!!channelId && messagesQuery.isLoading),
    sending,
    send,
  };
}
